import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { useAuthStore } from './lib/auth-store';
import { queryClient } from './lib/query-client';
import { initSentry } from './lib/sentry';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';

// 1. Sentry primeiro de tudo — no-op se VITE_SENTRY_DSN vazio (default).
//    Antes do auth store pra capturar erros já no boot do storage/JWT.
initSentry();

// 2. Inicializa o auth store ANTES do router. `beforeLoad` das rotas lê
//    `useAuthStore.getState().status` sincronamente — não pode estar
//    'initializing' no primeiro render.
useAuthStore.getState().init();

// 3. Cria o router. `defaultPreload: 'intent'` faz hover-prefetch
//    (TanStack Router default), bom o suficiente pro Cockpit.
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// 4. Type augmentation pro typed routing global.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found in index.html');
}

function ErrorFallback() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '1rem',
        fontFamily: 'system-ui, sans-serif',
        color: '#0f172a',
        gap: '0.5rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Algo deu errado no Cockpit.</h1>
      <p style={{ color: '#475569' }}>
        O erro foi reportado. Recarregar a página costuma resolver.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: '0.5rem',
          padding: '0.5rem 1rem',
          borderRadius: '0.375rem',
          background: '#0f172a',
          color: '#f8fafc',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Recarregar
      </button>
    </div>
  );
}

createRoot(container).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <RouterProvider router={router} />
        </NuqsAdapter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
